import AddCircleIcon from '@mui/icons-material/AddCircle'
import DescriptionIcon from '@mui/icons-material/Description'
import { Box, Card, CircularProgress, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { PlanOrganizationTaskName, PlanOrganizationTaskStatus } from '../../../../../models/PlanOrganizationTask'
import type { SupportCost } from '../../../../../models/SupportCost'
import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import type { ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { AddSupportCostModal } from './AddSupportCostModal'
import { TrackSupportCostModal } from './TrackSupportCostModal'
import { SubBasicTable } from '../SubBasicTable'
import { useModalState } from '../../../../../hooks/use-modalState'
import { DeleteItemModal } from '../../DeleteItemModal'
import { ClearTrackingModal } from '../../../implementation/ClearTrackingModal'
import { ClearSupportCostTracking, DeleteSupportCost, GetSupportCosts } from '../../../../../services/SupportCostService'

interface Props {
  annualPlanId: number
  readOnly: boolean
  rcnId: number
  status: string
  showTableOnly?: boolean
  color?: string
  isImplementationPhase?: boolean
  trackingReadOnly?: boolean
  budgetOverviewView?: boolean
}

type SupportCostRow = (SupportCost & {
  displayBudget: string
  expandedContent?: JSX.Element
})

export function SupportCosts (props: Props): JSX.Element {
  const [rows, setRows] = useState<SupportCostRow[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const isImplementationPhase = props.isImplementationPhase ?? false
  const { modalState, startModal, currentItem, handleCloseModal } = useModalState<SupportCost>(PlanOrganizationTaskName.SupportCost)

  useEffect(() => {
    void fetchSupportCosts()
  }, [])

  const expandedContent = (supportCost: SupportCost): JSX.Element | undefined => {
    if (!supportCost.isUpdate || supportCost.actualDollarAmount == null) return

    const subTableRows: [{ id: number, actualDollarAmount?: string, utilized: string }] = [{ id: supportCost.id, actualDollarAmount: `$${supportCost.actualDollarAmount ?? 0}`, utilized: supportCost.utilized === true ? 'Yes' : 'No' }]
    return <Box
      sx={{
        p: '2rem',
        backgroundColor: 'var(--grey-100)'
      }}
    >
      <SubBasicTable
        hasTrackingDetails
        onEditTracking={async () => { await startModal('track', supportCost) }}
        onDeleteTracking={async () => { await startModal('clearTrack', supportCost) }}
        columns={[
          { id: 'utilized', label: 'UTILIZED', minWidth: 200, isTrackingColumn: true },
          { id: 'actualDollarAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
          { id: 'trackingAction', label: '', isTrackingColumn: true }
        ]}
        rows={subTableRows}
        dataTestId={`${supportCost.id}-expandedFocusBuildingTable`}
      />
    </Box>
  }

  const fetchSupportCosts = async (): Promise<void> => {
    const items = await GetSupportCosts(props.annualPlanId, props.rcnId, !isImplementationPhase)
    setRows(items.map(cost => {
      const budget = cost.adjustedDollarAmount ?? cost.dollarAmount ?? 0

      return { ...cost, displayBudget: `$${budget}`, expandedContent: expandedContent(cost) }
    }))

    setIsLoading(false)
  }

  const addCostItemClick = (): void => {
    if (props.status === PlanOrganizationTaskStatus.NotStarted) {
      toast.error('Set the status to “In Progress” in order to continue.')
      return
    }

    void startModal('add')
  }

  const onSuccess = async (): Promise<void> => {
    setIsLoading(true)
    await fetchSupportCosts()

    switch (modalState) {
      case 'update':
        toast.success('RCN Support Cost Updated.')
        break
      case 'add':
        toast.success('RCN Support Cost Saved')
        break
      case 'track':
        toast.success('RCN Support Cost Tracked')
        break
      case 'clearTrack':
        toast.success('RCN Support Cost Tracking Removed')
        break
    }

    await handleCloseModal()
  }

  const columns: BasicTableColumn[] = [
    { id: 'description', label: 'Description', minWidth: 200 },
    { id: 'displayBudget', label: 'Budget', minWidth: 100 }
  ]

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'RCN Spending Guidelines',
      subTitle: <>Subject to the <a style={{ color: 'currentcolor' }} target='_blank' href='https://docs.google.com/document/d/1Ojt5fxeKvvCot5mERZaGGqihSdYFdoMw422SDIQkyFM/edit?tab=t.0'>RCN Contract Spending Guidelines</a>, RCN may budget for:</>,
      items: [
        <>Administrative support costs <i>directly</i> attributable to the performance of the RCN contract <b>to a maximum of 5%</b> of total contract expenditures.</>,
        <>RCN Leader/Personnel costs <i>directly</i> attributable to work performed to meet RCN contract priorities <b>to a maximum of 5%</b> of an individual’s FTE.</>,
        'Cost of creating and establishing an RCN website',
        'Cost of creating and distributing an RCN Newsletter',
        'Cost of services from other ISDs/Districts necessary to carry out contract activities (e.g., sub costs for trainers from other districts to provide trainings, time for Peer to Peer teachers to assist those looking to start a program).'
      ]
    }
  ]

  return <Box display='flex' flexDirection='column' gap='1em' data-testid='supportCosts'>
    <Typography variant='h4' color={props.color}>RCN Support Costs</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={addCostItemClick} dataTestId='addCostItemButton'>
            Add Support Cost <AddCircleIcon sx={{ ml: '0.5em' }} />
          </BlueButton>
    }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='supportCostThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='supportCostThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Card data-testid='cardContainer' sx={{ minHeight: '400px', width: '100%', display: rows.length === 0 ? 'grid' : 'block' }}>
      {isLoading
        ? <CircularProgress />
        : <>
          {rows.length === 0 &&
            <Box sx={{ justifySelf: 'center', justifyContent: 'center', alignContent: 'center', alignSelf: 'center', width: '300px' }}>
              <Box sx={{ mb: '.75em', width: '100%' }}>
                <Box sx={{ justifySelf: 'center' }}>
                  <DescriptionIcon sx={{ color: 'var(--blue-300)', width: '150px', height: '150px' }} />
                </Box>
                <Typography variant='h4'>No RCN Support Costs Added</Typography>
              </Box>
              {!props.readOnly &&
                <Typography variant='body1' sx={{ fontSize: '16px' }}>Use the Add Support Cost button to add RCN Support Costs.</Typography>}
            </Box>
          }

          {rows.length > 0 &&
            <BasicTable
              indexRowIds
              readonly={props.readOnly}
              dataTestId='supportCostTable'
              columns={columns}
              rows={rows}
              isLoading={false}
              onEdit={async (cost) => { await startModal('update', cost) }}
              onDelete={async (cost) => { await startModal('delete', cost) }}
              hideDelete={props.status === PlanOrganizationTaskStatus.UpdatesNeedsRevision || props.status === PlanOrganizationTaskStatus.TrackingInProgress}
              implementationPhase={isImplementationPhase}
              onTrack={async (cost) => { await startModal('track', cost) }}
              onUpdate={async (cost) => { await startModal('update', cost) }}
              trackingReadonly={props.trackingReadOnly}
              budgetOverviewView={props.budgetOverviewView}
            />
          }

        </>}
    </Card>

    {(modalState === 'delete' && currentItem != null) &&
      <DeleteItemModal
        item={currentItem}
        deleteFunction={DeleteSupportCost}
        onClose={handleCloseModal}
        onConfirm={onSuccess}
        itemType='Support Cost'
      />
    }

    {((modalState === 'track' && currentItem != null)) &&
      <TrackSupportCostModal
        orgId={props.rcnId}
        annualPlanId={props.annualPlanId}
        onClose={handleCloseModal}
        onSave={onSuccess}
        supportCost={currentItem}
      />
    }

    {(modalState === 'add' || (modalState === 'update' && (currentItem != null))) &&
      <AddSupportCostModal
        orgId={props.rcnId}
        annualPlanId={props.annualPlanId}
        onSave={onSuccess}
        onClose={handleCloseModal}
        isImplementationPhase={isImplementationPhase}
        supportCost={currentItem}
      />
    }

    {modalState === 'clearTrack' && currentItem != null &&
      <ClearTrackingModal
        onConfirm={async () => {
          await ClearSupportCostTracking({ id: currentItem.id })
          await onSuccess()
        }}
        onClose={handleCloseModal}
      />
    }

  </Box>
}
